<template>
  <div class="two-factor-step mt-2">
    <!-- Code from the authenticator app (or a recovery code) after the password -->
    <b-form v-if="stage === 'code'" @submit.prevent="verifyCode">
      <p class="mb-1">
        {{ $t(useBackupCode ? 'auth.login.two-factor.backup-description' : 'auth.login.two-factor.code-description') }}
      </p>
      <label class="form-label" for="two-factor-code">
        {{ useBackupCode ? $t('auth.login.two-factor.backup-label') : $t('auth.login.two-factor.code-label') }}
      </label>
      <b-form-input
        id="two-factor-code"
        :value="code"
        :placeholder="useBackupCode ? 'XXXX-XXXX-XXXX' : '123456'"
        :inputmode="useBackupCode ? 'text' : 'numeric'"
        autocomplete="one-time-code"
        autofocus
        @input="onCodeInput"
      />
      <b-alert
        v-if="error"
        show
        variant="danger"
        class="mt-1 mb-0"
      >
        {{ error }}
      </b-alert>
      <div class="auth-btn-container d-flex justify-content-center">
        <b-button
          type="submit"
          variant="primary"
          class="auth-btn auth-btn-primary mt-2 mb-1"
          :disabled="submitting || !codeComplete"
        >
          {{ submitting ? $t('auth.login.two-factor.verifying') : $t('auth.login.two-factor.verify') }}
        </b-button>
      </div>
      <p class="text-center mb-50">
        <b-link class="auth-link" @click="toggleBackupCode">
          {{ useBackupCode ? $t('auth.login.two-factor.use-app') : $t('auth.login.two-factor.use-backup') }}
        </b-link>
      </p>
      <p class="text-center">
        <b-link class="auth-link" @click="$emit('cancel')">
          {{ $t('auth.login.two-factor.back') }}
        </b-link>
      </p>
    </b-form>

    <div v-else-if="stage === 'loading'" class="text-center my-2">
      <b-spinner variant="primary" />
    </div>

    <!-- Mandatory enrollment for staff: scan, confirm with a code -->
    <b-form v-else-if="stage === 'scan'" @submit.prevent="activate">
      <h4 class="mb-50">
        {{ $t('auth.login.two-factor.enroll-title') }}
      </h4>
      <p>{{ $t('auth.login.two-factor.enroll-description') }}</p>
      <p class="mb-50">
        {{ $t('auth.login.two-factor.scan-qr') }}
      </p>
      <div class="text-center mb-1">
        <img :src="setup.qrCode" :alt="$t('auth.login.two-factor.qr-alt')" class="two-factor-step__qr">
      </div>
      <p class="small mb-25">
        {{ $t('auth.login.two-factor.manual-key') }}
      </p>
      <code class="d-block text-break mb-1">{{ setup.manualEntryKey }}</code>
      <label class="form-label" for="two-factor-enroll-code">{{ $t('auth.login.two-factor.enroll-code-label') }}</label>
      <b-form-input
        id="two-factor-enroll-code"
        :value="code"
        placeholder="123456"
        inputmode="numeric"
        autocomplete="one-time-code"
        @input="onCodeInput"
      />
      <b-alert
        v-if="error"
        show
        variant="danger"
        class="mt-1 mb-0"
      >
        {{ error }}
      </b-alert>
      <div class="auth-btn-container d-flex justify-content-center">
        <b-button
          type="submit"
          variant="primary"
          class="auth-btn auth-btn-primary mt-2 mb-1"
          :disabled="submitting || !codeComplete"
        >
          {{ submitting ? $t('auth.login.two-factor.activating') : $t('auth.login.two-factor.activate') }}
        </b-button>
      </div>
      <p class="text-center">
        <b-link class="auth-link" @click="$emit('cancel')">
          {{ $t('auth.login.two-factor.back') }}
        </b-link>
      </p>
    </b-form>

    <!-- Recovery codes are shown once; the enrollment login is over, so the user signs in again -->
    <div v-else-if="stage === 'codes'">
      <h4 class="mb-50">
        {{ $t('auth.login.two-factor.codes-title') }}
      </h4>
      <p>{{ $t('auth.login.two-factor.codes-description') }}</p>
      <div class="two-factor-step__codes mb-1">
        <code v-for="backupCode in backupCodes" :key="backupCode" class="d-block text-center">{{ backupCode }}</code>
      </div>
      <p class="text-center">
        <b-link class="auth-link mr-2" @click="copyCodes">
          {{ copied ? $t('auth.login.two-factor.copied') : $t('auth.login.two-factor.copy') }}
        </b-link>
        <b-link class="auth-link" @click="downloadCodes">
          {{ $t('auth.login.two-factor.download') }}
        </b-link>
      </p>
      <b-form-checkbox v-model="savedCodes" class="mb-1">
        {{ $t('auth.login.two-factor.confirm-saved') }}
      </b-form-checkbox>
      <p class="small">
        {{ $t('auth.login.two-factor.relogin-hint') }}
      </p>
      <div class="auth-btn-container d-flex justify-content-center">
        <b-button
          variant="primary"
          class="auth-btn auth-btn-primary mt-1"
          :disabled="!savedCodes"
          @click="$emit('cancel')"
        >
          {{ $t('auth.login.two-factor.relogin') }}
        </b-button>
      </div>
    </div>

    <div v-else>
      <b-alert show variant="danger">
        {{ error }}
      </b-alert>
      <p class="text-center">
        <b-link class="auth-link" @click="$emit('cancel')">
          {{ $t('auth.login.two-factor.back') }}
        </b-link>
      </p>
    </div>
  </div>
</template>

<script>
const TOTP_PATTERN = /^\d{6}$/;
const BACKUP_CODE_PATTERN = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

/**
 * Second step of a password login. `code`: the account has 2FA, ask for the TOTP or a recovery code.
 * `enroll`: staff account without 2FA; the backend only issued a session that can enroll, so enroll and log in again.
 */
export default {
  name: 'TwoFactorStep',

  props: {
    mode: {
      type: String,
      required: true,
      validator: (value) => ['code', 'enroll'].includes(value),
    },
    verificationToken: {
      type: String,
      default: null,
    },
    authApiUrl: {
      type: String,
      required: true,
    },
    communitySlug: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      stage: this.mode === 'code' ? 'code' : 'loading',
      code: '',
      useBackupCode: false,
      setup: null,
      backupCodes: [],
      savedCodes: false,
      copied: false,
      submitting: false,
      error: null,
    };
  },

  computed: {
    codeComplete() {
      return (this.useBackupCode ? BACKUP_CODE_PATTERN : TOTP_PATTERN).test(this.code);
    },
  },

  async created() {
    if (this.mode !== 'enroll') return;
    try {
      this.setup = await this.post('2fa/setup', {});
      this.stage = 'scan';
    } catch (error) {
      this.error = error.message;
      this.stage = 'failed';
    }
  },

  methods: {
    async post(path, body) {
      const response = await fetch(`${this.authApiUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = response.status === 401
          ? this.$t('auth.login.two-factor.errors.expired')
          : data.message || data.error || this.$t('auth.login.two-factor.errors.generic');
        throw new Error(message);
      }
      return data;
    },

    onCodeInput(value) {
      if (this.useBackupCode) {
        const compact = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
        this.code = (compact.match(/.{1,4}/g) || []).join('-');
      } else {
        this.code = String(value || '').replace(/\D/g, '').slice(0, 6);
      }
    },

    toggleBackupCode() {
      this.useBackupCode = !this.useBackupCode;
      this.code = '';
      this.error = null;
    },

    async verifyCode() {
      if (!this.codeComplete) {
        this.error = this.$t('auth.login.two-factor.errors.invalid-format');
        return;
      }
      this.submitting = true;
      this.error = null;
      try {
        const loginResponse = await this.post('2fa/verify', {
          verificationToken: this.verificationToken,
          code: this.code,
          communitySlug: this.communitySlug || undefined,
        });
        this.$emit('verified', loginResponse);
      } catch (error) {
        this.error = error.message;
      } finally {
        this.submitting = false;
      }
    },

    async activate() {
      if (!this.codeComplete) {
        this.error = this.$t('auth.login.two-factor.errors.invalid-format');
        return;
      }
      this.submitting = true;
      this.error = null;
      try {
        const result = await this.post('2fa/enable', { secret: this.setup.secret, token: this.code });
        this.backupCodes = result.backupCodes || [];
        this.stage = 'codes';
      } catch (error) {
        this.error = error.message;
      } finally {
        this.submitting = false;
      }
    },

    async copyCodes() {
      try {
        await navigator.clipboard.writeText(this.backupCodes.join('\n'));
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (error) {
        // Clipboard unavailable; the codes stay on screen and can be downloaded.
      }
    },

    downloadCodes() {
      const numbered = this.backupCodes.map((c, i) => `${i + 1}. ${c}`).join('\n');
      const content = `${this.$t('auth.login.two-factor.codes-title')}\n\n${numbered}\n`;
      const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = '2fa-backup-codes.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.two-factor-step__qr {
  width: 192px;
  height: 192px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
  background: #fff;
}

.two-factor-step__codes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  user-select: all;
}
</style>
